/**
 * KAN-924: кнопка "Вернуть в работу" для клиента с закрытым исходом (lost или won).
 * У lost без подтверждения; у won (Успех) с подтверждением: возврат снимает
 * продажу со статистики. Не рисуется у открытого клиента, у контакта в мусоре
 * (оттуда возвращает "Вернуть", KAN-898) и когда запрос закрыт пользователю
 * (useCanCallApi). Запрос, тост и обновление списков в useClientReopen.
 */
import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, type ConfirmView } from '@/components/telegram/ConfirmDialog';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { CLIENT_REOPEN_ACCESS_URL, useClientReopen } from '@/hooks/useClientReopen';
import { cn } from '@/lib/utils';
import type { Client } from '@/types/client';

const REOPEN_LABEL = 'Вернуть в работу';

const WON_CONFIRM: ConfirmView = {
  title: REOPEN_LABEL,
  description: 'Вернуть в работу? Успех будет снят.',
  confirm: REOPEN_LABEL,
};

interface ClientReopenButtonProps {
  clientId: string;
  outcome: Client['outcome'];
  /** Когда контакт убран в мусор (ISO); null/нет - не в мусоре. */
  junkAt: string | null | undefined;
  className?: string;
}

export function ClientReopenButton({ clientId, outcome, junkAt, className }: ClientReopenButtonProps): JSX.Element | null {
  const allowed = useCanCallApi('POST', CLIENT_REOPEN_ACCESS_URL);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const reopen = useClientReopen();
  const closed = outcome === 'lost' || outcome === 'won';
  if (!allowed || !closed || junkAt) return null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-testid="client-reopen"
        disabled={reopen.isPending}
        className={cn('text-gray-700', className)}
        onClick={() => (outcome === 'won' ? setConfirmOpen(true) : reopen.mutate(clientId))}
      >
        <RotateCcw className="mr-1 h-3.5 w-3.5" />
        {REOPEN_LABEL}
      </Button>
      {outcome === 'won' && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          view={WON_CONFIRM}
          onConfirm={() => reopen.mutate(clientId)}
          testId="client-reopen-confirm"
        />
      )}
    </>
  );
}
